import React, { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from '../common/Modal';

/**
 * Confirmation dialog for permanent account deletion.
 *
 * Deliberately requires typing a confirmation word: this is irreversible and
 * sits next to the logout button, so a mis-tap must not be enough. It is still
 * reachable in two clicks and completes in-app, as App Store Review Guideline
 * 5.1.1(v) requires.
 */
const DeleteAccountModal = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const confirmationPhrase = t('account.deleteConfirmWord');
  const canDelete =
    confirmationText.trim().toUpperCase() === confirmationPhrase.toUpperCase() && !isDeleting;

  const handleDelete = async () => {
    setError(null);
    setIsDeleting(true);
    const result = await onConfirm();
    setIsDeleting(false);

    if (!result?.ok) {
      setError(result?.error || t('account.deleteFailed'));
    }
    // On success the auth context signs the user out and this unmounts.
  };

  const handleClose = () => {
    if (isDeleting) return;
    setConfirmationText('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      labelledBy="delete-account-title"
      dismissible={!isDeleting}
      panelClassName="p-4 sm:p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 id="delete-account-title" className="text-xl font-bold text-gray-900">
            {t('account.deleteTitle')}
          </h3>
        </div>
        <button
          onClick={handleClose}
          disabled={isDeleting}
          className="w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-full transition disabled:opacity-40"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <p className="text-gray-600 mb-4">{t('account.deleteWarning')}</p>

      <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc list-inside">
        <li>{t('account.deleteItemAccount')}</li>
        <li>{t('account.deleteItemBoards')}</li>
        <li>{t('account.deleteItemImages')}</li>
        <li>{t('account.deleteItemLinks')}</li>
      </ul>

      <p className="text-xs text-gray-500 mb-5">{t('account.deleteRetention')}</p>

      <label htmlFor="delete-account-confirm" className="block text-sm font-medium text-gray-700 mb-2">
        {t('account.deleteConfirmPrompt', { word: confirmationPhrase })}
      </label>
      <input
        id="delete-account-confirm"
        type="text"
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        disabled={isDeleting}
        autoComplete="off"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:bg-gray-50"
        placeholder={confirmationPhrase}
      />

      {error && (
        <p role="alert" className="text-sm text-red-600 mb-4">
          {error}
        </p>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-3">
        <button
          onClick={handleClose}
          disabled={isDeleting}
          className="flex-1 px-4 py-3 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleDelete}
          disabled={!canDelete}
          className="flex-1 px-4 py-3 min-h-[44px] bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:bg-red-600"
        >
          {isDeleting && <Loader2 className="w-5 h-5 animate-spin" />}
          {isDeleting ? t('account.deleting') : t('account.deleteConfirmButton')}
        </button>
      </div>
    </Modal>
  );
};

export default DeleteAccountModal;
